import { get, sync } from 'vuex-pathify';

import { transform } from 'ol/proj';
import { GeoJSON } from 'ol/format';
import { unByKey } from 'ol/Observable';
import { Point, Polygon, GeometryCollection } from 'ol/geom';
import { circular } from 'ol/geom/Polygon.js';
import { getDistance } from 'ol/sphere.js';

import { getGeometryCentroid } from '@/assets/js/mapUtils';
import { defaultMinZoom } from '@/assets/js/variables';
import { getContext } from '@/assets/js/mapContext';

export default {
  computed: {
    activeTool: sync('tools/activeTool'),
    cursorPositionEpsg: get('map/cursorPositionEpsg'),
    isActiveToolNotReplaceable: sync('tools/isActiveToolNotReplaceable'),
    initialMapView: get('admin/settingsValues@initial_map_view'),
  },
  methods: {
    clearLayer(id, group) {
      this.getLayerById(id, group).getSource().clear();
    },
    createPolygonFromCircle(coordinates, geometry, projection, createCollection = false, vertices = 64) {
      if (!geometry)
        geometry = createCollection
          ? new GeometryCollection([new Polygon([]), new Point(coordinates[0])])
          : new Polygon([]);
      const center = transform(coordinates[0], projection, 'EPSG:4326');
      const last = transform(coordinates[1], projection, 'EPSG:4326');
      const circle = circular(center, getDistance(center, last), vertices).transform('EPSG:4326', projection);
      if (createCollection) {
        const geometries = geometry.getGeometries();
        geometries[0].setCoordinates(circle.getCoordinates());
        geometry.setGeometries(geometries);
      } else {
        geometry.setCoordinates(circle.getCoordinates());
      }
      return geometry;
    },
    setGeodesicCircleRadius(radius, feature, center, vertices = 64) {
      const geometry = feature.getGeometry();
      const centerCoordinates = transform(center, this.$_config.defaultEpsg, 'EPSG:4326');
      const circle = circular(centerCoordinates, radius, vertices).transform('EPSG:4326', this.$_config.defaultEpsg);
      switch (geometry.getType()) {
        case 'GeometryCollection': {
          const geometries = geometry.getGeometries();
          geometries[0].setCoordinates(circle.getCoordinates());
          geometry.setGeometries(geometries);
          feature.setGeometry(geometry);
          return;
        }
        case 'Polygon': {
          geometry.setCoordinates(circle.getCoordinates());
          feature.setGeometry(geometry);
          return;
        }
        default:
          return;
      }
    },
    createGeomAndFitView(geometry, options = {}) {
      const olGeometry = new GeoJSON().readGeometry(geometry, {
        dataProjection: geometry.crs.properties.name,
        featureProjection: this.$_config.defaultEpsg,
      });
      this.fitView(olGeometry, {
        ...options,
        ...(options.isDrawMarker ? { markerGeometry: getGeometryCentroid(olGeometry) } : {}),
      });
    },
    fitView(geometry, options = {}) {
      if (options.isDrawMarker) {
        this.drawMarker(options.markerGeometry, options);
      }
      let dataToFit = geometry;
      if (geometry instanceof GeometryCollection) dataToFit = geometry.getExtent();
      this.map.getView().fit(dataToFit, { duration: 500, maxZoom: 20, ...options });
    },
    getInteractionByName(name) {
      return this.map
        .getInteractions()
        .getArray()
        .find(interaction => interaction.get('name') == name);
    },
    getLayerById(id, group) {
      if (!group) {
        return this.map
          ?.getLayers()
          .getArray()
          .find(layer => layer.get('id') == id);
      } else {
        return this.map
          .getLayers()
          .getArray()
          .find(g => g.get('id') == group)
          .getLayersArray()
          .find(layer => layer.get('id') == id);
      }
    },
    getControlByName(name) {
      return this.map
        .getControls()
        .getArray()
        .find(control => control.get('name') === name);
    },
    getLayerByIdEmit({ id, group, callback }) {
      callback(this.getLayerById(id, group), {
        view: this.map.getView(),
      });
    },

    attachListener(eventType, callback, { type = 'once', cursor = 'crosshair' } = {}) {
      if (cursor) {
        this.map.getViewport().style.cursor = cursor;
      }
      this.listenerKey = this.map[type](eventType, callback);
    },
    deactivateAllToolsHandler() {
      if (!this.activeTool) {
        return;
      }
      let currentTool = JSON.parse(JSON.stringify(this.activeTool));
      if (currentTool.startsWith('identification')) currentTool = 'identification';
      else if (currentTool.startsWith('toolbar')) currentTool = 'toolbar';
      getContext(this).$emit(`${currentTool}-action`, false);
      this.deactivateToolHandler();
    },
    deactivateToolHandler(tool = this.activeTool) {
      if (!tool || tool !== this.activeTool) {
        return;
      }
      this.deactivateTool();
    },
    deactivateTool() {
      unByKey(this.listenerKey);
      this.map.getViewport().style.cursor = '';
      this.activeTool = undefined;
      this.isActiveToolNotReplaceable = false;
    },
    setMinZoom(zoom = defaultMinZoom) {
      this.map.getView().setMinZoom(zoom);
    },
    zoomView(isZoomIn, delta = 1) {
      const view = this.map.getView();
      const zoom = isZoomIn ? view.getZoom() + delta : view.getZoom() - delta;
      if (view.getMinZoom() && view.getMinZoom() > zoom) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.map.maxZoomReached'),
        });
        return;
      }
      this.map.getView().animate({ zoom, duration: 250 });
    },
  },
  mounted() {
    getContext(this).$off('getLayerById');
    getContext(this).$on('getLayerContextById', this.getLayerByIdEmit);
    getContext(this).$off('createGeomAndFitView');
    getContext(this).$on('createGeomAndFitView', this.createGeomAndFitView);
    getContext(this).$off('fitView');
    getContext(this).$on('fitView', this.fitView);
    getContext(this).$off('deactivateAllTools');
    getContext(this).$on('deactivateAllTools', this.deactivateAllToolsHandler);
  },
};
